/**
 * <ChildChip> — un enfant de la famille, dans la barre de l'espace parent (molecule).
 *
 * La chip dit de qui parle l'écran : le **prénom seul**. Sélectionnée, elle prend l'aplat choisi de
 * l'univers (`role-selected`, le marine du parent). Désactivée (une bascule de compte en cours), elle ne répond plus et garde son aspect.
 *
 * Pas d'avatar : une fratrie de quatre alignait quatre visages dans le header, qui pesaient plus
 * lourd que le contenu sous eux. Le prénom suffit à désigner l'enfant, et l'avatar reste là où il
 * dit quelque chose — en tête de la page.
 *
 * Ce n'est pas un `<Badge>` : le badge est une étiquette, la chip est un choix — elle se presse
 * et a un état sélectionné.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIParentChild } from "../../../ports/primary/ui/types"
import { Text } from "../../atoms/typography/Text"
import { inkOnFill } from "../../theme/tokens/colors"

export type ChildChipProps = {
    child: UIParentChild
    /** L'enfant dont l'écran parle — ou vers qui la bascule vient de partir. */
    selected?: boolean
    /** Une bascule est en cours : la chip ne répond plus, et garde son aspect. */
    disabled?: boolean
    onPress?: (child: UIParentChild) => void
    testID?: string
}

export function ChildChip(props: ChildChipProps) {
    const { child, selected = false, disabled = false, onPress, testID } = props

    const inert = disabled || selected

    return (
        <Pressable
            testID={testID}
            onPress={() => onPress?.(child)}
            disabled={inert}
            accessibilityRole="button"
            accessibilityLabel={child.firstname}
            accessibilityState={{ selected, disabled: inert }}
            style={({ pressed }: { pressed: boolean }) => [
                styles.chip(selected),
                pressed && !inert ? styles.pressed : undefined,
            ]}
        >
            <Text size="text-md" weight="bold" color={selected ? inkOnFill("role-selected") : "text-muted"}>
                {child.firstname}
            </Text>
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    /**
     * Pleine, sans contour ni relief : la chip du livre V13 se lit à son aplat. Non sélectionnée,
     * cet aplat est celui d'une CARTE (`surface-raised`), parce que la chip est posée sur le fond
     * de page : la lavande `surface-subtle`, faite pour se détacher du blanc d'une carte, y
     * disparaît (1,01:1 sur la couleur de page). Sur la lueur de l'accueil, le blanc se détache
     * un peu mieux que sur la page : 1,13:1 au coin, contre 1,08:1.
     */
    chip: (selected: boolean) => ({
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["4"],
        borderRadius: theme.radius.full,
        backgroundColor: selected ? theme.colors["role-selected"] : theme.colors["surface-raised"],
    }),
    pressed: {
        transform: [{ scale: 0.98 }],
    },
}))
